"use client"

import { Head } from "@inertiajs/react"
import { useMemo, useState } from "react"

import {
  CollabCoverImage,
  CollabFooterCta,
  CollabHeader,
  ContributorsStrip,
  FaqAccordion,
  ShareLinkChip,
  SuggestionComposer,
  SuggestionFeed,
} from "@/components/collabs"
import type { DeckState } from "@/components/collabs/suggestion-composer"
import { Badge } from "@/components/ui/badge"
import { RichText } from "@/components/ui/rich-text"
import { AuthedShell } from "@/layouts/authed-shell"
import { tagLabel } from "@/lib/tags"
import type { Brief } from "@/types"

interface CollabShowProps {
  brief: Brief
}

// A public collaboration, not a submission portal: one hero, the feed of
// what people have already brought (so a newcomer sees how their answer
// fits before being asked for it), one tactile composer (the
// conversation-card deck + form), and a quiet FAQ footer. The feed row
// carries a static credits/share aside — deliberately not sticky; the old
// sticky app-shell sidebar was the strongest remaining LMS signal on the
// page.
export default function CollabShow({ brief }: CollabShowProps) {
  const visibleTags = brief.taggings.filter((t) => t.is_visible)

  // The deck cycles suggestable prompts only; chosen slots live in the
  // feed as "On the playlist". Selection state lives here so feed chips
  // can deal the deck to their prompt.
  const suggestable = useMemo(
    () => brief.track_prompts.filter((p) => p.status.can_suggest),
    [brief.track_prompts],
  )
  const [deck, setDeck] = useState<DeckState>({ index: 0, direction: 0 })

  // Fresh props can retire prompts (a curator locks a slot); clamp rather
  // than yank the user back to card 1.
  const safeIndex =
    suggestable.length > 0 ? Math.min(deck.index, suggestable.length - 1) : 0

  const navigateDeck = (direction: 1 | -1) => {
    if (suggestable.length === 0) return
    setDeck({
      index: wrap(safeIndex + direction, suggestable.length),
      direction,
    })
  }

  const selectPrompt = (promptId: number) => {
    const target = suggestable.findIndex((p) => p.id === promptId)
    if (target === -1 || target === safeIndex) {
      scrollToComposer()
      return
    }
    setDeck({ index: target, direction: target > safeIndex ? 1 : -1 })
    scrollToComposer()
  }

  return (
    <AuthedShell headerVariant="light">
      <Head title={brief.title} />

      <div className="max-w-container mx-auto flex flex-col gap-12 px-4 py-10 lg:gap-16 lg:px-8 lg:py-14">
        {/* Hero — no boxed band; the Oat canvas and grain breathe. */}
        <section className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-10">
          <div className="flex flex-1 flex-col gap-8">
            <CollabHeader brief={brief} />

            {(Boolean(brief.strategic_context) || Boolean(brief.main_body)) && (
              <div className="flex max-w-xl flex-col gap-4">
                {brief.main_headline && (
                  <h2 className="text-primary text-lg font-semibold">
                    {brief.main_headline}
                  </h2>
                )}
                {brief.strategic_context && (
                  <p className="text-md text-secondary leading-relaxed">
                    {brief.strategic_context}
                  </p>
                )}
                {brief.main_body && (
                  <RichText
                    content={brief.main_body}
                    className="[&_p]:leading-relaxed"
                  />
                )}
              </div>
            )}

            {visibleTags.length > 0 && (
              <div className="flex max-w-xl flex-wrap gap-2">
                {visibleTags.map((tagging) => (
                  <Badge
                    key={tagging.id}
                    size="sm"
                    color="brand"
                    type="pill-color"
                  >
                    {tagLabel(tagging.tag)}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <CollabCoverImage brief={brief} />
        </section>

        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-12">
          <SuggestionFeed
            prompts={brief.track_prompts}
            onPromptSelect={selectPrompt}
          />
          <aside className="flex flex-col gap-8">
            <ContributorsStrip featured={brief.featured_contributors} />
            <ShareLinkChip url={brief.canonical_url} />
          </aside>
        </div>

        <SuggestionComposer
          brief={brief}
          prompts={suggestable}
          deck={{ index: safeIndex, direction: deck.direction }}
          onNavigate={navigateDeck}
        />

        {/* Quiet footer zone: FAQ after the section signature. The page's
            end-mark comes from PublicFooter. */}
        <div className="flex flex-col gap-10">
          <span aria-hidden className="scotch-rule" />

          <FaqAccordion faqs={brief.brief_faqs} />

          <CollabFooterCta participationState={brief.participation_state} />
        </div>
      </div>
    </AuthedShell>
  )
}

function scrollToComposer() {
  document
    .getElementById("track-prompts")
    ?.scrollIntoView({ behavior: "smooth", block: "start" })
}

function wrap(value: number, length: number) {
  return ((value % length) + length) % length
}
